import React from "react";
import "./PrintContinuum.scss";
import { getData } from "../../requests/CRUD";
import { TRAINING, TRAINING_CONTENT, PRICING_ANALYSIS } from "../../consts/tables";
import { sortArray } from "../../utils";
import { normalizeContentHtml } from "../EditorWrapper/normalizeContentHtml";

// Landscape, content-only print of an entire training course (every chapter and
// sub-chapter, at any nesting depth). Deliberately separate from PrintBooklet
// (the PDF-divider booklet, which owns the bundle's default `@page ... !important`)
// so the two views don't fight over the global @page rule — this view uses a
// NAMED landscape page plus runtime-injected default-page overrides.
export default class PrintContinuum extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            tree: [],
            contentMap: {},
            bookletData: { cover: null, backCover: null },
            error: null,
        };
    }

    componentDidMount() {
        if (typeof document !== 'undefined' && document.body) {
            document.body.classList.add('is-print-continuum');
        }
        this.injectLandscapePageStyle();
        this.fetchAll();
    }

    componentWillUnmount() {
        if (typeof document !== 'undefined' && document.body) {
            document.body.classList.remove('is-print-continuum');
        }
        this.removeLandscapePageStyle();
    }

    // Force the DEFAULT @page to landscape while this view is mounted, so the browser's
    // print dialog OPENS in Landscape by default. The dialog reads the bare (unnamed)
    // @page's `size` — and PrintBooklet.scss declares one with !important in the global
    // bundle (landscape today, but this view must not depend on it), and that wins here
    // too (the named continuum page can't override it: measured in Chrome, a bare
    // `size !important` beats even a named page's `size !important`).
    //
    // Chromium quirk (verified via headless print-to-PDF): when two @page rules both
    // declare `size` with !important, the FIRST in document order wins — the reverse of
    // the normal cascade. Spec-correct engines (and plain declarations) resolve
    // last-wins. So we inject the SAME landscape rule twice: one <style> PREPENDED as
    // the first node of <head> (wins under Chromium's first-wins quirk) and one
    // APPENDED last (wins under spec order). Landscape wins either way. Both are
    // removed on unmount so the booklet print keeps its portrait default.
    injectLandscapePageStyle() {
        if (typeof document === 'undefined') return;
        const css = [
            '@page { size: letter landscape !important; margin: 0.55in !important; }',
            '@page continuum-landscape { size: letter landscape !important; margin: 0.55in !important; }',
        ].join('\n');
        ['continuum-print-page-style-first', 'continuum-print-page-style-last'].forEach((id, i) => {
            if (document.getElementById(id)) return;
            const style = document.createElement('style');
            style.id = id;
            style.textContent = css;
            if (i === 0) document.head.insertBefore(style, document.head.firstChild);
            else document.head.appendChild(style);
        });
    }

    removeLandscapePageStyle() {
        if (typeof document === 'undefined') return;
        ['continuum-print-page-style-first', 'continuum-print-page-style-last'].forEach(id => {
            const el = document.getElementById(id);
            if (el && el.parentNode) el.parentNode.removeChild(el);
        });
    }

    get topSlug() {
        return (this.props.params && this.props.params.slug) || 'brokerage-continuum';
    }

    fetchAll() {
        const top = this.topSlug;

        // The whole section tree (every depth) via a LIKE match on PARENT_SLUG.
        const treeReq = getData({
            tableCode: TRAINING,
            options: {
                filter: [
                    { text: '', code: 'DELETED', type: 'null' },
                    { text: `${top}%`, code: 'PARENT_SLUG', type: 'match' },
                ],
                filterType: 'and',
            },
        });

        // Booklet assets (front/back covers) uploaded via /training-booklet.
        const assetReq = getData({
            tableCode: PRICING_ANALYSIS,
            options: {
                filter: [{ text: 'training-booklet', code: 'PRODUCT_TYPE', type: 'exact' }],
            },
        });

        Promise.all([treeReq, assetReq]).then(([res, assetRes]) => {
            let bookletData = { cover: null, backCover: null };
            if (assetRes && assetRes.success && assetRes.data && assetRes.data.length) {
                try {
                    bookletData = { ...bookletData, ...JSON.parse(assetRes.data[0].ITEMS) };
                } catch (e) {}
            }

            const rows = (res && res.success && Array.isArray(res.data)) ? res.data : [];
            const tree = this.buildTree(rows, top);

            // Collect every node's full slug so we can pull its content.
            const slugs = [];
            const walk = nodes => nodes.forEach(n => { slugs.push(n.fullSlug); walk(n.children); });
            walk(tree);

            const done = (contentMap = {}) => this.setState(
                { tree, contentMap, bookletData, loading: false },
                // Give fonts/images/PDF objects a beat to load, then open the print dialog.
                () => setTimeout(() => window.print(), 800)
            );

            if (!slugs.length) {
                done();
                return;
            }

            Promise.all(slugs.map(slug => getData({
                tableCode: TRAINING_CONTENT,
                options: {
                    filter: [
                        { text: '', code: 'DELETED', type: 'null' },
                        { text: slug, code: 'CHAPTER_SLUG', type: 'exact' },
                    ],
                    filterType: 'and',
                },
            }))).then(contentResList => {
                const contentMap = {};
                contentResList.forEach((r, i) => {
                    if (r && r.success && r.data && r.data.length) {
                        contentMap[slugs[i]] = r.data[0].CONTENT;
                    }
                });
                done(contentMap);
            });
        }).catch(() => this.setState({ error: "Failed to load continuum content.", loading: false }));
    }

    buildTree(rows, parentPath) {
        return sortArray(
            rows
                .filter(r => r.PARENT_SLUG === parentPath)
                .map(r => {
                    const fullSlug = `${parentPath}/${r.SLUG}`;
                    return {
                        id: r.SLUG,
                        name: r.NAME,
                        index: parseInt(r.INDEX),
                        fullSlug,
                        children: this.buildTree(rows, fullSlug),
                    };
                }),
            'index'
        );
    }

    // TinyMCE tends to leave trailing empty <p>/<br> that print as blank space.
    cleanContent(content) {
        if (!content) return '';
        return String(content).replace(
            /(?:\s|&nbsp;|<br\s*\/?>|<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>|<div>(?:\s|&nbsp;|<br\s*\/?>)*<\/div>)+$/gi,
            ''
        );
    }

    // Full-page front/back cover (image or PDF) uploaded in /training-booklet.
    renderCover(url, side) {
        if (!url) return null;
        const isPdf = String(url).toLowerCase().endsWith('.pdf');
        return (
            <div className={`continuum-cover continuum-cover--${side}`}>
                {isPdf
                    ? (
                        <object data={url} type="application/pdf">
                            <a href={url}>{side === 'front' ? 'Front' : 'Back'} cover (PDF)</a>
                        </object>
                    )
                    : <img src={url} alt={`${side} cover`} />}
            </div>
        );
    }

    // Chapter/subchapter divider (image or PDF) uploaded in /training-booklet; printed
    // under the node's title, filling the rest of that page.
    // The divider key is the node's slug path relative to the top course — the same key
    // BookletManager stores (e.g. "close", "close/close", "marketing/agent-database").
    renderDivider(node) {
        const dividers = this.state.bookletData.dividers || {};
        const top = this.topSlug;
        const key = node.fullSlug.startsWith(top + '/')
            ? node.fullSlug.slice(top.length + 1)
            : node.fullSlug;
        const url = dividers[key];
        if (!url) return null;
        const isPdf = String(url).toLowerCase().endsWith('.pdf');
        return (
            <div className="continuum-divider">
                {isPdf
                    ? (
                        <object data={url} type="application/pdf">
                            <a href={url}>{node.name} divider (PDF)</a>
                        </object>
                    )
                    : <img src={url} alt={`${node.name} divider`} />}
            </div>
        );
    }

    // Table of contents (chapters + their subchapters), printed right after the front
    // cover on its own page. No page numbers: browser print engines don't reliably
    // resolve cross-page reference counters, so this is a clean outline.
    renderToc() {
        const { tree } = this.state;
        if (!tree.length) return null;
        return (
            <section className="continuum-toc">
                <h1 className="continuum-toc__heading">Table of Contents</h1>
                <ol className="continuum-toc__list">
                    {tree.map(ch => (
                        <li key={ch.fullSlug} className="continuum-toc__chapter">
                            <span className="continuum-toc__chapter-name">{ch.name}</span>
                            {ch.children.length > 0 && (
                                <ul className="continuum-toc__subs">
                                    {ch.children.map(sub => (
                                        <li key={sub.fullSlug} className="continuum-toc__sub">{sub.name}</li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                </ol>
            </section>
        );
    }

    renderNode(node, depth) {
        const content = this.cleanContent(this.state.contentMap[node.fullSlug]);
        const Heading = depth === 0 ? 'h1' : depth === 1 ? 'h2' : 'h3';
        // The divider prints UNDER the node's title (filling the rest of that page);
        // the section's content resumes on the following page.
        return (
            <section
                key={node.fullSlug}
                className={`continuum-node continuum-node--d${depth}${depth === 0 ? ' continuum-chapter' : ''}`}
            >
                <Heading className={`continuum-title continuum-title--d${depth}`}>{node.name}</Heading>
                {this.renderDivider(node)}
                {content
                    ? <div className="continuum-content" dangerouslySetInnerHTML={{ __html: normalizeContentHtml(content) }} />
                    : null}
                {node.children.map(child => this.renderNode(child, depth + 1))}
            </section>
        );
    }

    render() {
        const { loading, tree, error, bookletData } = this.state;

        if (loading) return <div className="PrintContinuum_loading">Preparing continuum for print…</div>;
        if (error) return <div className="PrintContinuum_error">{error}</div>;

        return (
            <div className="PrintContinuum">
                {this.renderCover(bookletData.cover, 'front')}
                {this.renderToc()}
                {tree.map(ch => this.renderNode(ch, 0))}
                {this.renderCover(bookletData.backCover, 'back')}
            </div>
        );
    }
}
